import { IAcquisitionCell } from "../../interruptions/types";
import { IDictionary, IMachine, IShiftHistory } from "../../../types";

export enum ComponentType {
  Events,
  AcquisitionChart,
  Indicators,
  Radar,
  Doughnut,
  Gauge,
  Text,
  Custom
}

export enum Severity {
  Low,
  Medium,
  High
}

export interface IEventConfig {
  variables: IEventVariable[];
}

export interface IAcquisitionChartConfig  {
  acquisitionCell: IAcquisitionCell;
}

export interface IIndicatorConfig {
  variables: IIndicatorVariable[];
}

export interface IDoughnutConfig {
  scale?: number;
  variables: IDoughnutVariable[];
}

export interface IRadarConfig {
  scale?: number;
  variables: IRadarVariable[];
}

export interface IGaugeIndicatorConfig {
  scale?: number;
  variables: IGaugeVariable[];
}

export interface ICustomComponentConfig {
  code: string | null;
}

export interface IGripPosition {
  column: number;
  columnSpan:  number;
  row:  number;
  rowSpan:  number;
}

export interface IDashboardComponent<T> extends IGripPosition {
  key: string;
  title: string;
  componentType: ComponentType;
  config: T;
  background?: string;
  foreground?: string;
}

export interface ILayout {
  components: IDashboardComponent<any>[];
  palette: IDictionary<string>;
}

export interface IDashboard {
  dashboardID: number | null;
  moduleID: number;
  nodeType: string;
  nodeID: number;
  name: string;
  layout: ILayout;
  userID: number;
  owner: string | null;
  isPrivate: boolean;
  isDefault: boolean;
  isAcquisition?: boolean;
  isEditable?: boolean;
  customConfig: Record<string, any>;
  rtModule: boolean;
}

export interface IEventData {
  datetime: string;
  severity: Severity;
  message: string;
}

export interface IIndicatorVariable {
  hash: string;
  machine: string;
  code: string;
  title: string;
  color: string | null;
  minimum: number | null;
  maximum: number | null;
  unit: string | null;
  decimals: number;
  conditions?: ICondition[];
  hideChart?: true;
}


export interface IVariable {
  type: "VALUE" | "TAG";
  value: number | string;
}

export interface IGaugeSegment {
  start: IVariable;
  end: IVariable;
  color: string;
}

export interface IGaugeVariable extends IIndicatorVariable {
  segments: IGaugeSegment[];
}

export interface IDoughnutVariable {
  hash: string;
  machine: string;
  code: string;
  title: string;
  color: string | null;
  minimum: number | null;
  maximum: number | null;
  unit: string | null;
  decimals: number;
}

export interface IRadarLimit {
  type: "VALUE" | "TAG" | "NONE";
  value: number | string;
  comparison?: comparisonType;
  severity?: "error" | "warning" | "info";
}

export interface IRadarVariable{
  hash: string;
  machine: string;
  code: string;
  title: string;
  color: string | null;
  unit: string | null;
  minimum: number | null;
  maximum: number | null;
  decimals: number;
  limits: {
    minimum: IRadarLimit;
    maximum: IRadarLimit;
    aim: IRadarLimit;
  };
}


export interface IIndicators extends IIndicatorVariable{
  value: number | string | boolean;
  status: "error" | "warning" | "info" | null;
  lastPoints: number[];
}

export type comparisonType = ">" | ">=" | "<" | "<=" | "=" | "<>";


export interface IEventCondition {
  hash: string;
  title: string;
  type: "VALUE" | "TAG" ;
  value: number | string;
  comparison: comparisonType;
  severity: Severity;
}

export interface IEventVariable {
  hash: string;
  machine: string;
  code: string;
  conditions: IEventCondition[];
}

export interface ICondition extends IVariable {
  hash: string;
  machine: string;
  comparison: comparisonType;
  severity: "error" | "warning" | "info" | "none";
}

export interface IGanttProduction {
  productionMachineID: number;
  reference: string | null;
  beginDatetime: number;
  endDatetime: number | null;
}

export interface IGanttDelay {
  delayID: number;
  beginDatetime: number;
  endDatetime: number | null;
  isDownDay: boolean;
  productionReference: string;
}

export interface IGanttEvents {
  group: string;
  name: string;
  reference: string;
  datetime: number;
  variables: {
    variable: string;
    value: number;
    sortIndex: number;
  }[];
  type: number;
}

export interface IProductionMachine {
  machine: IMachine;
  production: IGanttProduction[];
  delays: IGanttDelay[];
  events: IGanttEvents[];
}

export interface IDashboardGanttData {
  shiftHistory: IShiftHistory[];
  machines: IProductionMachine[];
}

export interface ICategory {
  categoryID: number;
  catalogID: number;
  name: string;
  hash: string;
  version: number;
  disabled: boolean;
}

export interface IFamilyGroup {
  familyGroupID: number;
  familyID: number;
  productID: number | null;
  productType: "STEEL" | "WIRE_ROD";
}

export interface IFamily {
  familyID: number;
  name: string;
  description: string;
  disabled: boolean;
  groups: IFamilyGroup[];
}

export interface ICatalog {
  catalogID: number;
  familyGroupID: number | null;
  machineID: number | null;
  categories: ICategory[];
}